import { useTranslation } from 'react-i18next';
import {
  getAllQuickReplies,
  matchSlashQuery,
  filterQuickReplies,
  applyQuickReply,
  type QuickReply,
} from '../utils/quickReplies';

interface Props {
  content: string;
  onSelect: (text: string) => void;
  highlightIdx: number;
  onHover: (i: number) => void;
}

export default function QuickRepliesBar({
  content,
  onSelect,
  highlightIdx,
  onHover,
}: Props) {
  const { t } = useTranslation();
  const allReplies = getAllQuickReplies(t);
  const slashQuery = matchSlashQuery(content);
  const isOpen = slashQuery !== null;
  const filtered = isOpen ? filterQuickReplies(allReplies, slashQuery) : [];

  const pick = (reply: QuickReply) => {
    onSelect(applyQuickReply(content, reply));
  };

  if (!isOpen) return null;

  return (
    <SlashMenu
      filtered={filtered}
      highlightIdx={highlightIdx}
      onPick={pick}
      onHover={onHover}
      t={t}
    />
  );
}

function SlashMenu({
  filtered,
  highlightIdx,
  onPick,
  onHover,
  t,
}: {
  filtered: QuickReply[];
  highlightIdx: number;
  onPick: (r: QuickReply) => void;
  onHover: (i: number) => void;
  t: (k: string) => string;
}) {
  return (
    <div
      role="listbox"
      aria-label={t('quickReplies.slashHint')}
      className="absolute bottom-full inset-x-0 mb-1 bg-surface-0 rounded-lg shadow-elevated border border-surface-200 py-1 max-h-48 overflow-y-auto z-30"
    >
      {filtered.length === 0 ? (
        <p className="px-3 py-2 text-xs text-surface-500">{t('quickReplies.noMatch')}</p>
      ) : (
        filtered.map((reply, idx) => (
          <button
            key={reply.id}
            type="button"
            onClick={() => onPick(reply)}
            onMouseEnter={() => onHover(idx)}
            role="option"
            aria-selected={idx === highlightIdx}
            className={`flex items-center w-full px-3 py-2 text-sm text-start transition-colors duration-150 ${
              idx === highlightIdx ? 'bg-surface-100 text-surface-900' : 'hover:bg-surface-50 text-surface-800'
            }`}
          >
            <span className="flex-1 leading-snug">{reply.text}</span>
          </button>
        ))
      )}
    </div>
  );
}
